import React from 'react';
import constants from '@/constants/const';
import { ProductType, ShopMoreType } from '@/types/product';
import { CartItemV2, PerchaseItemReq } from '@/types/so';
import { PositionInfoT2Param } from '@/types/track';
import callAppFunction from '@/utils/bridge';
import { isSayWeeeApp } from '@/utils/weeeapp';
import { isAppSupportPurchaseStatusNotify as isAppSupportPurchaseStatus } from '@/utils/weeeapp-notify';
import { getCookie } from '@/utils/cookie';
import { CardType } from '../ProductCard/types';
import { customAddCartApi } from '@/types/cms';
import { Toast } from '@/components/common/CommonToast';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

const PreSaleCartConsumer = dynamic(() => import('@/components/common/PreSaleCartConsumer'), {
  ssr: false
});

const CartConsumer = dynamic(() => import('@/components/common/CartConsumer'), {
  ssr: false
});

const PreSaleCartBtn = dynamic(() => import('@/components/pre-sale/PreSaleCartBtn'), {
  ssr: false
});

const AddCart = dynamic(() => import('./add-cart'), {
  ssr: false
});

const PreSaleSuccessToast = dynamic(() => import('@/components/pre-sale/PreSaleSuccessToast'), {
  ssr: false
});

export interface AddCartProps {
  num?: number;
  //每次增加的数量
  count?: number;
  text?: string;
  imageUrl?: string;
  className?: string;
  data?: ProductType | CartItemV2;
  is?: ProductType;
  isNotification?: boolean;
  source?: string; // TODO source 字段 首次优先：如果一个商品已经被加入购物车设置好source，那么在后续其他页面调整其数量，并不修改source；
  referType?: string;
  referValue?: string;
  customReferType?: string;
  customReferValue?: string;
  deliveryDate: string;
  id2qty?: ObjectType;
  /**加购按钮是否显示文案 */
  renderCartCountText?: boolean;
  render?: any;
  onClick?: (type?, num?) => void;
  isInCart?: boolean;
  addCart?: (data: CartItemV2, count: number) => void;
  subCart?: (data: CartItemV2, count: number) => void;
  /**delivery组件 根据productId获取商品的配送日期 */
  productId?: number;
  minLimitArrowToTarget?: number;
  preSell?: boolean;
  changeDeliveryDateButtonText?: string;
  link?: string;
  onNumChange?: (goodsInfo?) => void;
  immediatelyCallChangeNum?: boolean;
  isInProductCard?: boolean;
  showTooltip?: boolean;
  positionInfoT2?: PositionInfoT2Param;
  isAppSupportPurchaseStatusNotify?: boolean;
  index?: number;
  trackDataInDialog?: Record<string, any>;
  clickTracking?: () => void;
  useNewUpdateCartApi?: boolean; // 是否使用新的更新购物车接口 items/cart/v3
  customAddCartApi?: customAddCartApi; // 自定义购物车接口
  defaultExpand?: boolean;
  useMkplCartApi?: boolean; // 是否使用mkpl的更新购物车接口
  isFetchSimpleOrder?: boolean; //加购是否重新获取porder/simple接口
  /** 自定义加购接口 */
  addCartApiFn?: (params: any) => any;
  addOnce?: boolean; // 重置加购按钮状态
  // 自定义加购tooltip样式
  customTooltipCls?: string;
  onBeforeAddCart?: () => void;
  needShowPreSaleModal?: boolean;
  shopMoreType?: ShopMoreType;
  refValue?: string | null;
  onAddError?: (res?: ObjectType, goodsInfo?: ObjectType[]) => void;
  isHoriziontal?: boolean;
  remindedFontClassName?: string;
  remindFontClassName?: string;
  changeOtherDayFontClassName?: string;
  onSuccess?: (data: ObjectType) => void;
}

// 通知APP更新购物车数据
const notifyAppToUpdate = (params?: PerchaseItemReq[], isAppSupportPurchaseStatusNotify?: boolean) => {
  try {
    if (isSayWeeeApp()) {
      const isAppSupportPurchaseStatusNotifyVersion = isAppSupportPurchaseStatus();
      if (isAppSupportPurchaseStatusNotify && isAppSupportPurchaseStatusNotifyVersion) {
        const args = {
          products: params
        };
        callAppFunction('purchaseStatusNotify', args);
      } else {
        callAppFunction('preOrderStatusNotify', {
          pre_order_id: getCookie(constants.PRE_ORDER_ID),
          token: getCookie(constants.ORDER_TOKEN),
          status: 'propertyChanged'
        });
      }
    }
  } catch (error) {
    console.log('notifyAppToUpdate error', error);
  }
};

const AddCartEnhance = ({
  isInCart,
  useNewUpdateCartApi,
  customAddCartApi,
  isAppSupportPurchaseStatusNotify,
  onNumChange,
  trackDataInDialog,
  useMkplCartApi,
  isFetchSimpleOrder = false,
  addCartApiFn,
  immediatelyCallChangeNum,
  shopMoreType,
  refValue,
  onAddError,
  ...rest
}: AddCartProps & CardType) => {
  const router = useRouter();
  const isPreSale = rest?.data && !!Object?.keys?.(rest?.data)?.length && rest?.data?.is_presale;

  const handleAddError = (res: ObjectType, params: ObjectType[]) => {
    onAddError?.(res, params);
    Toast({
      text: res?.message,
      showIcon: false,
      position: 'center',
      className: 'text-center',
      innerClassName: '!w-auto max-w-[340px] inline-flex'
    });
  };

  const handleShowSuccessToast = (data, productId) => {
    if (!data || !Object.keys(data).length) return;
    Toast({
      text: (
        <PreSaleSuccessToast data={data} productId={productId} pageUrl={router.asPath || ''} routerPush={router.push} />
      ),
      showIcon: false,
      position: 'bottom',
      className: 'text-center px-5 bottom-[120px] z-[1009]',
      innerClassName: '!w-full max-w-[350px] inline-flex bg-transparent p-0',
      textClassName: '!w-full',
      duration: 2000
    });
  };

  if (isPreSale) {
    return (
      <>
        <PreSaleCartConsumer
          trackDataInDialog={trackDataInDialog}
          customAddCartApi={customAddCartApi}
          useNewUpdateCartApi={useNewUpdateCartApi}
          isInCart={isInCart}
          isFetchSimpleOrder={isFetchSimpleOrder}
          isAppSupportPurchaseStatusNotify={isAppSupportPurchaseStatusNotify}
          immediatelyCallChangeNum={immediatelyCallChangeNum}
          changeNum={(params, isAppSupportPurchaseStatusNotify) => {
            onNumChange?.(params);
            notifyAppToUpdate(params, isAppSupportPurchaseStatusNotify);
          }}
          addCartApiFn={addCartApiFn}
          onError={handleAddError}
          onAddSuccess={handleShowSuccessToast}
          shopMoreType={shopMoreType}
          refValue={refValue}>
          <PreSaleCartBtn {...rest} customAddCartApi={customAddCartApi} isFetchSimpleOrder={isFetchSimpleOrder} />
        </PreSaleCartConsumer>
      </>
    );
  }

  return (
    <CartConsumer
      trackDataInDialog={trackDataInDialog}
      customAddCartApi={customAddCartApi}
      useNewUpdateCartApi={useNewUpdateCartApi}
      isInCart={isInCart}
      isFetchSimpleOrder={isFetchSimpleOrder}
      isAppSupportPurchaseStatusNotify={isAppSupportPurchaseStatusNotify}
      immediatelyCallChangeNum={immediatelyCallChangeNum}
      changeNum={(params, isAppSupportPurchaseStatusNotify) => {
        onNumChange?.(params);
        notifyAppToUpdate(params, isAppSupportPurchaseStatusNotify);
      }}
      addCartApiFn={addCartApiFn}
      shopMoreType={shopMoreType}
      refValue={refValue}
      onError={handleAddError}>
      <AddCart {...rest} customAddCartApi={customAddCartApi} isFetchSimpleOrder={isFetchSimpleOrder} />
    </CartConsumer>
  );
};

export default AddCartEnhance;
